import { type ReactNode, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import type { CollectorSummary, ProjectedGroupBonus } from '../types';
import { rankingChip, type RankingEntry } from '../utils/bonus-indicators';
import { BonusChipView } from './bonus-chip-view';
import { RaceChipCard } from './race-chip-card';
import { GroupBonusRaceCard } from './group-bonus-progress';
import { HistoryButton } from './bonus-history-modal';
import { RankingHistoryModal } from './ranking-history-modal';
import { GroupHistoryModal } from './group-history-modal';
import { RaceHistoryModal } from './race-history-modal';

type Props = {
  ranking: RankingEntry[];
  summary: CollectorSummary;
};

type OpenState = {
  ranking: boolean;
  group: boolean;
  race: boolean;
};

const INITIAL: OpenState = { ranking: false, group: false, race: false };

function useHistoryModals() {
  const [open, setOpen] = useState<OpenState>(INITIAL);
  const set = (key: keyof OpenState, v: boolean): void =>
    setOpen((prev) => ({ ...prev, [key]: v }));
  return { open, set };
}

function Modals({
  open,
  set,
}: {
  open: OpenState;
  set: (k: keyof OpenState, v: boolean) => void;
}): ReactNode {
  return (
    <>
      <RankingHistoryModal
        open={open.ranking}
        onClose={() => set('ranking', false)}
      />
      <GroupHistoryModal
        open={open.group}
        onClose={() => set('group', false)}
      />
      <RaceHistoryModal open={open.race} onClose={() => set('race', false)} />
    </>
  );
}

type GridProps = {
  rChip: ReturnType<typeof rankingChip>;
  gb: ProjectedGroupBonus | undefined;
  set: (k: keyof OpenState, v: boolean) => void;
};

function ChipGrid(p: GridProps): ReactNode {
  return (
    <div className="grid grid-cols-1 gap-md md:grid-cols-2 lg:grid-cols-3">
      <BonusChipView
        chip={p.rChip}
        headerRight={<HistoryButton onClick={() => p.set('ranking', true)} />}
      />
      <GroupBonusRaceCard
        gb={p.gb}
        headerRight={<HistoryButton onClick={() => p.set('group', true)} />}
      />
      <RaceChipCard
        headerRight={<HistoryButton onClick={() => p.set('race', true)} />}
      />
    </div>
  );
}

export function BonusIndicators(props: Props): ReactNode {
  const { user } = useAuth();
  const rChip = rankingChip(props.ranking, user?.id ?? '');
  const gb = props.summary.provisional_this_week?.group_bonus_projected;
  const { open, set } = useHistoryModals();
  return (
    <div>
      <h3 className="mb-sm mt-xl text-base font-semibold uppercase tracking-wider text-text-primary">
        Bonuses semanales
      </h3>
      <ChipGrid rChip={rChip} gb={gb} set={set} />
      <Modals open={open} set={set} />
    </div>
  );
}
